import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { useMarketingMotion } from "./MarketingMotion";

// Byte-mirrors core/marketing.py DEFAULTS["hero"]["taglines"] (POS-2 2026-08-18: the
// "…Seamlessly." tagline retired for the registry-safe outcome phrasing).
export const HERO_TAGLINES = Object.freeze([
  "AI, Governed by People.",
  "One Platform. Admissions to Fees, Connected.",
  "From Enquiry to Enrolment. One Record.",
  "See What Matters. Act With Confidence.",
]);

export const HERO_TYPEWRITER_TIMING = Object.freeze({
  initialHold: 2200,
  hold: 1750,
  erase: 24,
  gap: 180,
  type: 44,
  punctuation: 130,
});

function normalizeTaglines(taglines, fallbackText) {
  const source = Array.isArray(taglines) ? taglines : [];
  const seen = new Set();
  const normalized = [];

  source.forEach((value) => {
    if (typeof value !== "string") return;
    const phrase = value.trim();
    const key = phrase.toLocaleLowerCase("en-IN");
    if (!phrase || seen.has(key)) return;
    seen.add(key);
    normalized.push(phrase);
  });

  if (normalized.length > 0) return normalized;
  const fallback = typeof fallbackText === "string" ? fallbackText.trim() : "";
  return fallback ? [fallback] : [HERO_TAGLINES[0]];
}

function useDocumentVisible() {
  const [visible, setVisible] = useState(() => (
    typeof document === "undefined" || document.visibilityState === "visible"
  ));

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const update = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  return visible;
}

function useElementVisible(ref) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(Boolean(entry?.isIntersecting)),
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return visible;
}

function initialMachine(phrases) {
  return {
    index: 0,
    characters: phrases[0]?.length || 0,
    phase: phrases.length > 1 ? "initial-hold" : "settled",
    completedCycles: 0,
  };
}

/**
 * A finite, motion-governed hero typewriter.
 *
 * The animated copy is hidden from assistive technology so a screen reader never
 * announces individual characters. The first phrase remains as one stable SR-only
 * string, and invisible phrase copies reserve the tallest/widest wrapped layout.
 */
export default function HeroTaglineRotator({
  className = "",
  fallbackText = HERO_TAGLINES[0],
  // `loop` (product decision 2026-08-21): cycle continuously WHILE ATTENDED instead of settling
  // after `maxCycles`. This stays finite-in-effect and WCAG 2.2.2-conformant because the same
  // `running` gate governs every tick: prefers-reduced-motion renders the static line, the footer
  // "Pause animations" switch freezes it, and the document/element visibility gates stop it the
  // moment the tab is hidden or the hero scrolls away — motion follows attention, it never churns
  // in the background. With `loop` false the classic type-once-then-settle contract is unchanged.
  loop = false,
  maxCycles = 1,
  taglines = HERO_TAGLINES,
}) {
  const phraseKey = normalizeTaglines(taglines, fallbackText).join("\u001f");
  const phrases = useMemo(() => phraseKey.split("\u001f"), [phraseKey]);
  const rootRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const documentVisible = useDocumentVisible();
  const elementVisible = useElementVisible(rootRef);
  const [machine, setMachine] = useState(() => initialMachine(phrases));

  useEffect(() => {
    setMachine(initialMachine(phrases));
  }, [phrases]);

  const cycleLimit = Math.max(1, Number.isFinite(maxCycles) ? Math.floor(maxCycles) : 1);
  const running = (
    !prefersReducedMotion
    && !paused
    && documentVisible
    && elementVisible
    && phrases.length > 1
    && machine.phase !== "settled"
  );

  useEffect(() => {
    if (!running) return undefined;

    const phrase = phrases[machine.index] || "";
    let delay = 0;
    let next = machine;

    if (machine.phase === "initial-hold") {
      delay = HERO_TYPEWRITER_TIMING.initialHold;
      next = { ...machine, phase: "deleting" };
    } else if (machine.phase === "deleting") {
      if (machine.characters > 0) {
        delay = HERO_TYPEWRITER_TIMING.erase;
        next = { ...machine, characters: machine.characters - 1 };
      } else {
        delay = HERO_TYPEWRITER_TIMING.gap;
        const atLastPhrase = machine.index === phrases.length - 1;
        if (!atLastPhrase) {
          next = {
            ...machine,
            index: machine.index + 1,
            characters: 0,
            phase: "typing",
          };
        } else {
          const completedCycles = machine.completedCycles + 1;
          next = {
            index: 0,
            characters: 0,
            completedCycles,
            phase: !loop && completedCycles >= cycleLimit ? "settle-typing" : "typing",
          };
        }
      }
    } else if (machine.phase === "typing" || machine.phase === "settle-typing") {
      if (machine.characters < phrase.length) {
        const nextCharacter = phrase[machine.characters] || "";
        delay = /[.!?,;:]/.test(nextCharacter)
          ? HERO_TYPEWRITER_TIMING.punctuation
          : HERO_TYPEWRITER_TIMING.type;
        next = { ...machine, characters: machine.characters + 1 };
      } else if (machine.phase === "settle-typing") {
        next = { ...machine, phase: "settled" };
      } else {
        delay = HERO_TYPEWRITER_TIMING.hold;
        next = { ...machine, phase: "deleting" };
      }
    }

    const timer = window.setTimeout(() => setMachine(next), delay);
    return () => window.clearTimeout(timer);
  }, [cycleLimit, loop, machine, phrases, running]);

  const accessibleText = phrases[0] || fallbackText;
  const visibleText = prefersReducedMotion
    ? accessibleText
    : (phrases[machine.index] || "").slice(0, machine.characters);
  const settled = prefersReducedMotion || machine.phase === "settled";

  return (
    <span ref={rootRef} className={`hero-tagline-rotator ${className}`.trim()}>
      <span className="sr-only">{` ${accessibleText}`}</span>
      <span
        aria-hidden="true"
        className="hero-tagline-rotator__stage"
        data-running={running ? "true" : "false"}
        data-settled={settled ? "true" : "false"}
      >
        {phrases.map((phrase) => (
          <span className="hero-tagline-rotator__reserve" key={phrase}>{phrase}</span>
        ))}
        <span className="hero-tagline-rotator__visible" data-testid="hero-typewriter-text">
          {visibleText || "\u00a0"}
          <span className="hero-tagline-rotator__cursor" />
        </span>
      </span>
    </span>
  );
}
